import React, { useMemo } from "react";
import {
  CollapseItem,
  MenuItem,
  MenuList,
  SubMenuItem,
  useSidebar,
} from "@8x8/oxygen-sidebar-menu";
import { Link, useMatches, useNavigate } from "react-router-dom";
import { usePermissions } from "@/react/services/rbac/usePermissions";
import UserIcon from "@8x8/oxygen-icon/icons/UserIcon";
import { FeedbackIcon, NewTabIcon } from "@8x8/oxygen-icon/icons";
import { LogoLink } from "@/react/components/side-nav/LogoLink";
import { useSelector } from "react-redux";
import { userSelector } from "@/react/store/userSlice";
import { EVENT_TYPES, triggerEvent } from "@/events";
import UserHostIcon from "@8x8/oxygen-icon/icons/UserHostIcon";
import { customerSelector } from "@/react/features/customer/customerSlice";
import { JOBRUNR_READ } from "@/react/services/rbac/permissions";
import { API_V2_URL, SMP_DIAGNOSTICS_URL } from "@/vue/store/urls";
import { AppVersion } from "@/react/components/side-nav/AppVersion";
import { LogoutButton } from "@/react/components/side-nav/LogoutButton";
import { sendInfo } from "@/react/observability";

import { PrintDisabledSidebarContainer } from "./PrintDisabledSidebarContainer";
import Debug from "@/react/debug";

const hasLabel = (route) => route.label !== undefined;

const permissionFilterBuilder = (hasPermission) => (route) =>
  route.permission ? hasPermission(route.permission) : true;

export const SideNav = ({ routes }) => {
  const isIframe = useSelector((state) => state.application.isIframe);
  const user = useSelector(userSelector);
  const customer = useSelector(customerSelector);
  const customerName = customer?.customerName;

  const matches = useMatches();
  const { isSidebarCollapsed } = useSidebar();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const toPath = (route) => {
    const message = `[SideNav] item "${route.label}" clicked`;
    sendInfo(message);
    Debug.log(message);

    navigate(route.path);
  };

  const isActive = (path) => matches.some((match) => match.pathname === path);

  const filteredRoutes = useMemo(
    () =>
      routes
        .filter(hasLabel)
        .filter(permissionFilterBuilder(hasPermission))
        .map((r) => {
          if (!r.children) {
            return r;
          }
          return {
            ...r,
            children: r.children
              .filter(hasLabel)
              .filter(permissionFilterBuilder(hasPermission)),
          };
        }),
    [routes, hasPermission]
  );

  return (
    <PrintDisabledSidebarContainer
      data-test-id={"sidebar-menu"}
      isIframe={isIframe}
      collapsed={isSidebarCollapsed}
    >
      <Link to="/">
        <LogoLink isCompact={isSidebarCollapsed} />
      </Link>
      <MenuList data-test-id={"menu-list"}>
        {filteredRoutes.map((route) => (
          <MenuItem
            key={route.path}
            testId={"sidenav"}
            label={route.label || ""}
            icon={route.icon}
            onTrigger={() => toPath(route)}
            isActive={isActive(route.path)}
          >
            {route.children?.map((child) => (
              <SubMenuItem
                key={child.path}
                testId={"sidenav-child"}
                label={child.label || ""}
                onTrigger={() => toPath(child)}
                isActive={isActive(child.path)}
              />
            ))}
          </MenuItem>
        ))}
        <MenuItem
          icon={<NewTabIcon />}
          testId={"sidenav"}
          label="SMP Diagnostics"
          onTrigger={() => window.open(SMP_DIAGNOSTICS_URL, "_blank").focus()}
        />
        {hasPermission(JOBRUNR_READ) && (
          <MenuItem
            icon={<NewTabIcon />}
            testId={"sidenav"}
            label="JobRunr Dashboard"
            onTrigger={() =>
              window.open(`${API_V2_URL}/jobrunr/dashboard`, "_blank").focus()
            }
          />
        )}
      </MenuList>
      <CollapseItem label="Collapse" />
      <MenuList footer>
        {customerName && (
          <MenuItem
            testId={"sidenav-footer"}
            label={`Customer: ${customerName}`}
            icon={<UserHostIcon />}
            onTrigger={() => triggerEvent(EVENT_TYPES.OPEN_CUSTOMER_SELECT)}
          />
        )}
        {user?.name && (
          <>
            <MenuItem
              testId={"sidenav-footer"}
              label={user?.name || ""}
              icon={<UserIcon />}
            />
            <LogoutButton />
          </>
        )}
        <MenuItem
          testId={"sidenav-footer"}
          label="Send feedback"
          icon={<FeedbackIcon />}
          onTrigger={() => triggerEvent(EVENT_TYPES.OPEN_FEEDBACK)}
        />
        <AppVersion isCompact={isSidebarCollapsed} />
      </MenuList>
    </PrintDisabledSidebarContainer>
  );
};
